import React from 'react'
import Head from 'next/head'

import 'dayjs/locale/id'
import { Text } from '@wartek-id/text'

import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import { useGetTopicList } from 'api/microlearning/useAPITopics'
import {
  EmptyState,
  EmptyStateAction,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import DesktopLandingShell from '../components/DesktopLandingShell'
import TabbedNav from '../components/TabbedNav'
import { shouldHideNav } from '../helper'
import { usePelatihanMandiriDesktopView } from '../hooks/usePelatihanMandiriDesktopView'
import WebinarCard, { WebinarPreloader } from '../partials/Webinar/WebinarCard'
import {
  filterAvailableWebinars,
  getUserEmailFromSession,
  getWebinarTopicData,
} from '../utils/webinar'

import type { WebinarEntry } from 'api/microlearning/types'

import WebinarNotFound from 'assets/images/webinar-not-found.svg'

const WebinarList = ({ isDesktop = false, currentId = '' }) => {
  const { data, status } = useGetKeyPairValue(MILKvKeys.WebinarEntries)
  const { data: topics } = useGetTopicList()
  const userSession = useLocalStorage('app-login', undefined)
  const userEmail = getUserEmailFromSession(userSession)
  const isPelatihanMandiriDesktopViewEnabled = usePelatihanMandiriDesktopView()
  const shouldUseDesktopView = isDesktop && isPelatihanMandiriDesktopViewEnabled
  const webinarDataFiltered: WebinarEntry[] =
    status === 'success'
      ? filterAvailableWebinars(data?.data?.webinar ?? [], userEmail)
      : []
  const isWebinarEmpty = status === 'success' && webinarDataFiltered.length <= 0
  const webinarListClassName = (() => {
    if (shouldUseDesktopView) {
      return isWebinarEmpty
        ? 'flex min-h-[520px] items-center justify-center text-center'
        : 'grid gap-3 md:grid-cols-2 xl:grid-cols-3'
    }

    return isWebinarEmpty
      ? 'flex min-h-[calc(100vh-9rem)] items-center justify-center px-4 pt-4 text-center'
      : 'px-4 pt-4'
  })()

  const RenderWebinarList = () => (
    <div className={webinarListClassName}>
      {status === 'loading' &&
        [...Array(4)].map((_arr, index) => (
          <div key={`webinar-preloader-${index}`} className="mb-4">
            <WebinarPreloader />
          </div>
        ))}
      {status === 'success' &&
        webinarDataFiltered.map(
          ({
            id,
            title,
            topic,
            speakers,
            startDate,
            endDate,
            module,
          }: WebinarEntry) => {
            const topicData = getWebinarTopicData(
              {
                topikId: topic?.id,
                topikTitle: topic?.title,
              },
              (topics?.data || []).map(({ id }) => +id),
              module
            )

            return (
              <div
                key={`webinar-card-${id}`}
                className={shouldUseDesktopView ? 'mb-4 h-full' : 'mb-4'}
              >
                <WebinarCard
                  id={id}
                  href={`/${
                    process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                    'pelatihan-mandiri'
                  }/webinar/${id}`}
                  topicId={`${topicData?.topikId}`}
                  topicName={topicData?.topikTitle}
                  title={title}
                  speakers={speakers}
                  startDate={startDate}
                  endDate={endDate}
                  active={currentId === id}
                  className={shouldUseDesktopView ? 'h-full' : undefined}
                  titleClassName={
                    shouldUseDesktopView
                      ? 'line-clamp-2 min-h-[calc(var(--size-line-height-text-heading-sm)*2)]'
                      : undefined
                  }
                />
              </div>
            )
          }
        )}
      {status === 'error' && (
        <EmptyState
          fullscreen={!shouldUseDesktopView}
          className={shouldUseDesktopView ? 'min-h-[320px]' : undefined}
          title="Data gagal dimuat"
          description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
        >
          <EmptyStateAction
            className={shouldUseDesktopView ? 'mb-4 max-w-sm' : undefined}
            onClick={() => window.location.reload()}
          >
            Muat Ulang
          </EmptyStateAction>
        </EmptyState>
      )}
      {isWebinarEmpty && (
        <EmptyStateWithIllustration
          fullscreen={false}
          className="max-w-[400px] items-center px-0 text-center"
          title="Jadwal Webinar Kosong"
          description="Belum ada Webinar yang akan berlangsung untuk saat ini, silakan coba akses halaman ini lagi di lain waktu."
        >
          <WebinarNotFound
            data-testid="webinar-empty-state-image"
            as="EmptyStateImageSvg"
          />
        </EmptyStateWithIllustration>
      )}
    </div>
  )

  if (shouldUseDesktopView) {
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Daftar Webinar</title>
        </Head>
        <DesktopLandingShell activeMenu="webinar">
          <section className="rounded-xl bg-gray-5 p-6 shadow-sm">
            <Text variant="display-sm" as="h2" className="mb-4">
              Daftar Webinar
            </Text>
            <RenderWebinarList />
          </section>
        </DesktopLandingShell>
      </>
    )
  }
  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Daftar Webinar</title>
      </Head>
      {!shouldHideNav() && (
        <TabbedNav activeMenu="webinar" className="top-[56px]" />
      )}
      <div
        className="overflow-y-auto"
        style={{ height: 'calc(100vh - 3.5rem)' }}
      >
        <div className={shouldHideNav() ? 'pt-0' : 'pt-9'}>
          <RenderWebinarList />
        </div>
      </div>
    </>
  )
}

export default WebinarList
